import { createAtlAttributionHeader } from '../fetch/atl-attribution-header';
import { fetchAggResponse } from '../fetch/fetch-agg-response';

import { localStore } from './local-store';
import { logger } from './logger';

interface QuickSearchResponse {
	search: {
		results: {
			edges: Array<{
				node: {
					type: string;
					id: string;
					title: string;
					url: string;
					iconUrl: string;
				};
			}>;
		};
	};
}

interface QuickSearchVariables {
	query: string;
	first: number;
	experience: string;
	filters: {
		entities: string[];
		locations: string[];
	};
}

/**
 * Attempts to resolve a Jira URL from the local store
 * @param issueKey - The Jira issue key to look up eg PROJ-123
 * @returns The URL if found in local store, null otherwise
 */
const resolveFromLocalStore = (issueKey: string): string | null => {
	const storedUrl = localStore.getString('jira-issue-url');
	if (!!storedUrl && new RegExp(issueKey, 'i').test(storedUrl)) {
		return storedUrl;
	}
	return null;
};

/**
 * Attempts to resolve a Jira URL from the quicksearch endpoint
 * @param issueKey - The Jira issue key to look up eg PROJ-123
 * @returns The URL if found in quicksearch results, null otherwise
 */
const resolveFromQuickSearch = async ({
	issueKey,
	cloudId,
	workspaceId,
	product,
}: {
	issueKey: string;
	cloudId: string;
	workspaceId: string;
	product: string;
}): Promise<string | null> => {
	try {
		const response = await fetchAggResponse<QuickSearchVariables, QuickSearchResponse>({
			graphQuery: `
			query KnowledgeDiscoveryIssueSearch(
				$experience: String!
				$query: String!
				$filters: SearchFilterInput!
				$first: Int
			) {
				search {
					results: search(
						experience: $experience
						query: $query
						filters: $filters
						first: $first
					) {
						edges {
							node {
								type
								id
								title
								url
								iconUrl
								... on SearchResultJiraIssue {
									issue {
										key
									}
								}
							}
						}
					}
				}
			}
			`,
			variables: {
				experience: 'knowledge-discovery.issue-card',
				query: issueKey,
				filters: {
					entities: ['ati:cloud:jira:issue'],
					locations: [`ari:cloud:jira::site/${cloudId}`],
				},
				first: 1,
			},
			operationName: 'KnowledgeDiscoveryIssueSearch',
			headers: createAtlAttributionHeader({
				atlWorkspaceId: workspaceId,
				tenantId: cloudId,
				product,
			}),
		});

		const results = response.data?.search?.results?.edges ?? [];
		const matchingResult = results.find(
			(result: {
				node: {
					type: string;
					id: string;
					title: string;
					url: string;
					iconUrl: string;
					issue?: { key: string };
				};
			}) => result.node.issue?.key?.toLowerCase() === issueKey.toLowerCase(),
		);

		return matchingResult?.node?.url || null;
	} catch (error) {
		logger.error({ error, functionName: 'resolveFromQuickSearch' });
	}
	return null;
};

/**
 * Resolves a Jira ticket ID to its corresponding URL using multiple resolution strategies
 * @param issueKey - The Jira issue key to look up eg PROJ-123
 * @returns The full URL to the Jira ticket, or null if no URL is found
 */
export const resolveJiraUrl = async ({
	issueKey,
	cloudId,
	workspaceId,
	product,
}: {
	issueKey: string;
	cloudId: string;
	workspaceId: string;
	product: string;
}): Promise<string | null> => {
	// Try local store first
	const localStoreUrl = resolveFromLocalStore(issueKey);
	if (!!localStoreUrl) {
		return localStoreUrl;
	}
	// Fall back to quicksearch
	const quickSearchUrl = await resolveFromQuickSearch({ issueKey, cloudId, workspaceId, product });
	return quickSearchUrl;
};
